import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import IconButton from '@material-ui/core/IconButton';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import Popover from '@material-ui/core/Popover';
import { withStyles } from '@material-ui/core/styles';
import { AuthenticationDialog } from '../containers/AuthenticationDialog';
import { SignUpPromo } from './SignUpPromo';
import { UserMenu } from '../containers/UserMenu';
import Cookie from 'js-cookie';


const styles = (theme) => ({
  avatarButton: {
    margin: '0em 0.1em 0',
    opacity: 1,
    transition: 'opacity 0.2s linear',
    '&:hover': {
      opacity: 1,
    },
  },
  avatarIcon: {
    color: '#fff',
    height: '1.25em',
    width: '1.25em',
  },
  avatarImage: {
    background: 'rgba(0, 0, 0, 0) center no-repeat',
    backgroundSize: 'cover',
    borderRadius: '50%',
    overflow: 'hidden',
  },
  popoverPaper: {
    // hide on mobile landscape fullscreen
    [`${theme.customBreakpoints.down('md1')} and (orientation: landscape)`]: {
      display: 'none',
    },
    // center at mobile widths
    [`${theme.breakpoints.down('md')} and (orientation: portrait)`]: {
      left: '50%!important',
      transform: 'translate(-50%, 0)!important',
    },
  },
});

class UserAccount extends PureComponent {
  static propTypes = {
    userAvatar: PropTypes.string,
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    fetchAll: PropTypes.func.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    loginWithEmail: PropTypes.func.isRequired,
    loginWithFacebookToken: PropTypes.func.isRequired,
    loginWithGoogleToken: PropTypes.func.isRequired,
    loginWithStoredToken: PropTypes.func.isRequired,
    loginWithTwitterToken: PropTypes.func.isRequired,
    logout: PropTypes.func.isRequired,
    registerWithEmail: PropTypes.func.isRequired,
    requestPasswordRecovery: PropTypes.func.isRequired,
    showAuthenticationDialog: PropTypes.func.isRequired,
    hideAuthenticationDialog: PropTypes.func.isRequired,
    isShowAuthenticationDialog: PropTypes.bool.isRequired,
    changePasswordStoredToken: PropTypes.func.isRequired,
    updateEmailStoredToken: PropTypes.func.isRequired,
  };

  static defaultProps = {
    userAvatar: null,
  };

  state = {
    anchorEl: null,
    isNewUser: false,
    justSignedIn: false,
    openProfile: false,
    selectedTab: 'signUp',
    recoveryEmail: '',
    updatedEmail: '',
    googleLoginErrorMsg: '',
    googleLoginDisabled: false,
    generalErrorMsg: '',
  };

  async componentWillMount() {
    await this.props.loginWithStoredToken();
  }

  // show success page
  afterAuth() {
    this.props.fetchAll();
    this.setState({ justSignedIn: true });
    this.handleDialogTabChange(null, 'loginSuccess');
  }

  openDialogOn(selectedTab) {
    return () =>
      this.setState(
        {
          openProfile: false,
          selectedTab,
        },
        () => {
          this.props.showAuthenticationDialog();
        },
      );
  }

  handleOpenDialogOnDevices = this.openDialogOn('devices');
  handleOpenDialogOnChatSettings = this.openDialogOn('chatSettings');

  handleCloseProfilePopup = () => {
    this.setState({ openProfile: false, justSignedIn: false });
    this.props.hideAuthenticationDialog();
  };

  handleDialogTabChange = (event, selectedTab) => {
    if (event) event.preventDefault();
    this.setState({
      openProfile: false,
      provider: null,
      selectedTab,
      values: null,
    });
    this.props.showAuthenticationDialog();
  };

  handleMouseOverProfile = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleOpenProfilePopup = (event) => {
    event.preventDefault();
    this.setState({ openProfile: true });
  };

  handleForgotPassword = async (data) => {
    await this.props.requestPasswordRecovery(data);
    if (!this.props.apiError) {
      this.setState({
        selectedTab: 'forgotPasswordSuccess',
        recoveryEmail: data.email,
      });
    }
  };

  handleCreatePassword = async (data) => {
    await this.props.requestPasswordRecovery(data);
    if (!this.props.apiError) {
      this.setState({
        selectedTab: 'createPasswordSuccess',
        recoveryEmail: data.email,
      });
    }
  };

  handleSignIn = async (data) => {
    await this.props.loginWithEmail(data);
    if (this.props.isAuthenticated) {
      this.afterAuth();
    }
  };

  handleSignOut = () => {
    this.setState({ openProfile: false });
    this.props.hideAuthenticationDialog();
    this.props.logout();
  };

  handleSignUp = async (data) => {
    // check if user is under 14 years
    const userIsUnder14 = () => {
      const thisYear = (new Date()).getFullYear();
      return parseInt(data.birthday.getFullYear(), 10) + 14 >= thisYear;
    }

    // if user is under 14, drop cookie, show err, early exit
    if (userIsUnder14()) {
      const twoHours = 2*60*60*1000;
      let expiration = new Date();
      expiration.setTime(expiration.getTime() + twoHours);
      Cookie.set('coppa_underage', 1, {path: '/', expires: expiration});
      this.setState({generalErrorMsg: 'Sorry, an error occured.'})
      return;
    }

    // proceed as usual to make sign up request
    await this.props.registerWithEmail(data);
    if (this.props.isAuthenticated) {
      this.setState({ isNewUser: true });
      this.afterAuth();
    }
  };

  // currently below function is only being used for google sign in and is tailored
  // to that experience
  handleSocialAuth = async (provider, data) => {
    if (provider === 'Google') {
      await this.props.loginWithGoogleToken(data);
    } else {
      throw new Error('invalid provider');
    }

    if (data.error) {
      if (data.error === 'idpiframe_initialization_failed') {
        this.setState({ googleLoginDisabled: true });
      } else if (data.error === 'popup_closed_by_user') {
        if (this.state.googleLoginDisabled) {
          this.setState({
            googleLoginErrorMsg: 'Please enable third party cookies to use Google Sign In.',
          });
        }
      }
    } else if (this.props.isAuthenticated) {
      this.afterAuth();
    } else {
      this.setState({ selectedTab: 'signUp' });
    }
  };

  handleUpdateEmail = async (data) => {
    await this.props.updateEmailStoredToken(data);
    if (!this.props.apiError) {
      this.setState({
        selectedTab: 'updateEmailSuccess',
        updatedEmail: data.email,
      });
    }
  };

  handleChangePassword = async (data) => {
    await this.props.changePasswordStoredToken(data);
    if (!this.props.apiError) {
      this.setState({
        selectedTab: 'changePasswordSuccess',
      });
    }
  };

  render() {
    const { userAvatar, classes, isAuthenticated } = this.props;
    const { isNewUser, justSignedIn, anchorEl, ...state } = this.state;

    return (
      <div>
        <IconButton
          className={classes.avatarButton}
          onClick={this.handleOpenProfilePopup}
          onFocus={this.handleMouseOverProfile}
          onMouseOver={this.handleMouseOverProfile}
        >
          {userAvatar ? (
            <div
              className={classNames(classes.avatarIcon, classes.avatarImage)}
              style={{ backgroundImage: `url('${userAvatar}')` }}
            />
          ) : (
            <AccountCircleIcon className={classes.avatarIcon} />
          )}
        </IconButton>

        {/* triggered by clicking account icon not signed in */}
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl && (!isAuthenticated || justSignedIn) && this.state.openProfile)}
          onClose={this.handleCloseProfilePopup}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          classes={{ paper: classes.popoverPaper }}
        >
          {!isAuthenticated && <SignUpPromo openDialog={this.handleDialogTabChange} />}
        </Popover>

        {/* triggered by clicking account icon when signed in */}
        <UserMenu
          anchorEl={this.state.anchorEl}
          hasChatUser={!!userAvatar}
          onClose={this.handleCloseProfilePopup}
          open={isAuthenticated && !justSignedIn && this.state.openProfile}
          openDialog={this.handleDialogTabChange}
          openDialogOnDevices={this.handleOpenDialogOnDevices}
          openDialogChatSettings={this.handleOpenDialogOnChatSettings}
          updatedEmail={this.state.updatedEmail}
        />

        {/* account popup */}
        <AuthenticationDialog
          logout={this.handleSignOut}
          open={this.props.isShowAuthenticationDialog}
          openDialog={this.handleDialogTabChange}
          onClose={this.handleCloseProfilePopup}
          onForgotPassword={this.handleForgotPassword}
          onCreatePassword={this.handleCreatePassword}
          onSignIn={this.handleSignIn}
          onSignUp={this.handleSignUp}
          onSocialAuth={this.handleSocialAuth}
          onTabChange={this.handleDialogTabChange}
          onUpdateEmail={this.handleUpdateEmail}
          onChangePassword={this.handleChangePassword}
          updatedEmail={this.state.updatedEmail}
          recoveryEmail={this.state.recoveryEmail}
          googleLoginErrorMsg={this.state.googleLoginErrorMsg}
          generalErrorMsg={this.state.generalErrorMsg}
          {...state}
        />
      </div>
    );
  }
}

export const UserAccountComponent = withStyles(styles)(UserAccount);
